import type { FleetAuthorizationStatus, FleetStatus } from '@goelectric/shared-types';
import {
  darkColors,
  neutralColors,
  semanticColors,
  type StatusIconKey,
} from '@goelectric/ui-components';
import { StatusBadgeShell, StatusIcon, useStatusColor } from '@/components/StatusBadgeShell';

interface FleetStatusMeta {
  label: string;
  description: string;
  color: string;
  colorDark: string;
  iconKey: StatusIconKey;
}

/** Estado operativo de la flota (la flota es una empresa: etiquetas en femenino). */
const FLEET_STATUS_META: Record<FleetStatus, FleetStatusMeta> = {
  ACTIVE: {
    label: 'Activa',
    description: 'Flota habilitada para operar y consumir del saldo de la Cuenta Maestra.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  INACTIVE: {
    label: 'Inactiva',
    description: 'Flota deshabilitada en el CRM.',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

/** Estado del vehículo (mismo enum `FleetStatus`, etiquetas en masculino). */
const FLEET_VEHICLE_STATUS_META: Record<FleetStatus, FleetStatusMeta> = {
  ACTIVE: {
    label: 'Activo',
    description: 'Vehículo habilitado y con su RFID autorizado.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  INACTIVE: {
    label: 'Inactivo',
    description: 'Vehículo dado de baja; su RFID queda bloqueado.',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

/**
 * Estado de sincronización del RFID del vehículo en los tenants de CitrineOS (ADR 0047).
 * `PARTIAL` es un estado de advertencia: requiere reconciliación, pero no es una falla total.
 */
const FLEET_AUTHORIZATION_META: Record<FleetAuthorizationStatus, FleetStatusMeta> = {
  ACTIVE: {
    label: 'Autorizado',
    description: 'La tarjeta RFID autoriza en todos los tenants relevantes.',
    color: semanticColors.success,
    colorDark: darkColors.success,
    iconKey: 'check',
  },
  BLOCKED: {
    label: 'Bloqueado',
    description: 'La autorización RFID está bloqueada (vehículo inactivo).',
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'lock',
  },
  PARTIAL: {
    label: 'Parcial',
    description: 'La tarjeta solo está replicada en algunos tenants; requiere reconciliación.',
    color: semanticColors.warning,
    colorDark: darkColors.warning,
    iconKey: 'info',
  },
  ERROR: {
    label: 'Error de autorización',
    description: 'Falló la replicación del RFID en los tenants; requiere reconciliación.',
    color: semanticColors.danger,
    colorDark: darkColors.danger,
    iconKey: 'warning',
  },
};

function Badge({
  meta,
  forceDark,
}: {
  meta: FleetStatusMeta;
  forceDark?: boolean;
}) {
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={meta.label}
      title={`${meta.label}: ${meta.description}`}
    />
  );
}

/** Estado de la flota: color + icono + texto (WCAG, nunca solo color). */
export function FleetStatusBadge({
  status,
  forceDark = false,
}: {
  status: FleetStatus;
  forceDark?: boolean;
}) {
  return <Badge meta={FLEET_STATUS_META[status]} forceDark={forceDark} />;
}

/** Estado del vehículo de la flota. */
export function FleetVehicleStatusBadge({
  status,
  forceDark = false,
}: {
  status: FleetStatus;
  forceDark?: boolean;
}) {
  return <Badge meta={FLEET_VEHICLE_STATUS_META[status]} forceDark={forceDark} />;
}

/** Estado de autorización/sincronización del RFID del vehículo. */
export function FleetAuthorizationBadge({
  status,
  forceDark = false,
}: {
  status: FleetAuthorizationStatus;
  forceDark?: boolean;
}) {
  return <Badge meta={FLEET_AUTHORIZATION_META[status]} forceDark={forceDark} />;
}
